import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, FileText, Sparkles } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { KpiCard } from '../components/common/KpiCard';
import { SectionCard } from '../components/common/SectionCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { ForecastPanel } from '../components/analytics/ForecastPanel';
import { ScoreDistribution } from '../components/analytics/ScoreDistribution';
import { CycleTimeChart } from '../components/analytics/CycleTimeChart';
import { CriterionRadar } from '../components/analytics/CriterionRadar';
import { GateForecastChart } from '../components/analytics/GateForecastChart';
import { RecommendationImpact } from '../components/analytics/RecommendationImpact';
import { SupportBySector } from '../components/analytics/SupportBySector';
import { EmirateTrackFlow } from '../components/analytics/EmirateTrackFlow';
import { AnomalyTable } from '../components/analytics/AnomalyTable';
import { ModelPerformance } from '../components/analytics/ModelPerformance';
import { ReportDocument } from '../components/ai/ReportDocument';
import { usePlatform } from '../contexts/PlatformContext';
import { modelPerformance, anomalies } from '../data/analytics';
import { summariseGate } from '../utils/stages';
import { reportTypeLabels } from '../utils/reportBuilder';
import { formatDate } from '../utils/format';
export function Analytics() {
  const {
    applications,
    reports,
    rules
  } = usePlatform();
  const live = applications.filter(record => record.status !== 'rejected');
  const gateSummaries = live.map(record => summariseGate(record.gates[record.stage] ?? []));
  const cleared = gateSummaries.filter(summary => summary.total > 0 && summary.cleared).length;
  const withGates = gateSummaries.filter(summary => summary.total > 0).length;
  const meanScore = live.reduce((sum, r) => sum + r.aiScore, 0) / Math.max(live.length, 1);
  const openRecommendations = live.reduce((sum, record) => sum + record.recommendations.filter(item => item.status === 'pending').length, 0);
  const criticalFlags = live.reduce((sum, record) => sum + record.flags.filter(flag => flag.severity === 'critical').length, 0);
  const latestReport = reports[0];
  return <div data-id="element-1124" data-mp="2k3ts7h1lm29e">
      <PageHeader eyebrow="Intelligence" title="AI analytics" description="Portfolio intelligence generated across every record: scoring behaviour, stage performance, forecasts, anomaly detection, model accuracy and report output." actions={<>
            <Link to="/intelligence" className="inline-flex items-center rounded-sm border border-border bg-card px-3 py-2 text-[13px] font-medium transition-colors hover:bg-muted" data-id="element-1126" data-mp="37to2pryg3eqh">
              <Sparkles className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-1127" data-mp="1di9czb5fpfaq" />
              Intelligence hub
            </Link>
            <Link to="/reports" className="inline-flex items-center rounded-sm bg-uae-navy px-3 py-2 text-[13px] font-medium text-white transition-colors hover:bg-uae-sky" data-id="element-1128" data-mp="zawl59wx70by">
              Reports archive
              <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-1129" data-mp="pk1u7s8qbck6" />
            </Link>
          </>} data-id="element-1125" data-mp="2k3238p2ib2s3" />

      <div className="space-y-4 p-6" data-id="element-1130" data-mp="uhtnoqp0v6qh">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6" data-id="element-1131" data-mp="fm5nrdqql5se">
          <KpiCard label="Mean AI score" value={meanScore.toFixed(1)} accent="green" meta={`Across ${live.length} live records`} data-id="element-1132" data-mp="fp0711y0kkk8" />
          <KpiCard label="Gate clearance rate" value={`${withGates === 0 ? 0 : Math.round(cleared / withGates * 100)}%`} accent="leaf" meta={`${cleared} of ${withGates} current gates cleared`} data-id="element-1133" data-mp="6o24ko384l3r" />
          <KpiCard label="Forecast accuracy" value={`${modelPerformance.gateForecastAccuracy}%`} accent="navy" meta="Gate outcomes predicted correctly" data-id="element-1134" data-mp="25y1uj4bhl47y" />
          <KpiCard label="Open recommendations" value={String(openRecommendations)} accent="gold" meta={`${rules.filter(rule => rule.active).length} rules active`} data-id="element-1135" data-mp="z23or4xa22j" />
          <KpiCard label="Critical flags" value={String(criticalFlags)} accent="red" tone="risk" meta={`${anomalies.length} anomalies detected`} data-id="element-1136" data-mp="3o4jc5gljozun" />
          <KpiCard label="Review time saved" value={`${modelPerformance.meanReviewTimeSavedHours}h`} accent="sky" meta="Mean per assessment, versus manual baseline" data-id="element-1137" data-mp="3eau70p4xonhw" />
        </div>

        <ForecastPanel data-id="element-1138" data-mp="30d5mjies3idd" />

        <Tabs defaultValue="portfolio" data-id="element-1139" data-mp="30ccn72i5fv59">
          <TabsList className="flex-wrap" data-id="element-1140" data-mp="2vdt0gzvqefbu">
            <TabsTrigger value="portfolio" data-id="element-1141" data-mp="2vf414qtvr6yq">Portfolio analytics</TabsTrigger>
            <TabsTrigger value="predictive" data-id="element-1142" data-mp="1awjqt7blt3pr">Predictive & anomalies</TabsTrigger>
            <TabsTrigger value="model" data-id="element-1143" data-mp="1ful9hr54b10p">Model assurance</TabsTrigger>
            <TabsTrigger value="reporting" data-id="element-1144" data-mp="1fwy0suwrmnqf">Report output</TabsTrigger>
          </TabsList>

          <TabsContent value="portfolio" className="mt-4 space-y-4" data-id="element-1145" data-mp="1jx84u20zz42n">
            <EmirateTrackFlow records={live} data-id="element-1146" data-mp="1ougok71sng60" />
            <div className="grid gap-4 xl:grid-cols-2" data-id="element-1147" data-mp="1ot5mqcrwsihj">
              <ScoreDistribution records={applications} data-id="element-1148" data-mp="s6ahzelbadp3" />
              <CycleTimeChart data-id="element-1149" data-mp="w6udyeu5fb9n" />
            </div>
            <div className="grid gap-4 xl:grid-cols-2" data-id="element-1150" data-mp="w7nid95mxoom">
              <CriterionRadar data-id="element-1151" data-mp="116787kncnkm9" />
              <SupportBySector records={live} data-id="element-1152" data-mp="114w2vge9a4t1" />
            </div>
          </TabsContent>

          <TabsContent value="predictive" className="mt-4 space-y-4" data-id="element-1153" data-mp="15wxh37uzi5lp">
            <div className="grid gap-4 xl:grid-cols-2" data-id="element-1154" data-mp="8hjhvanqu51u">
              <GateForecastChart data-id="element-1155" data-mp="8gqhcrfdobs5" />
              <RecommendationImpact data-id="element-1156" data-mp="dgcvd3315w20" />
            </div>
            <AnomalyTable records={applications} data-id="element-1157" data-mp="des1zgsgcj5q" />
          </TabsContent>

          <TabsContent value="model" className="mt-4 space-y-4" data-id="element-1158" data-mp="1gqgypxy5f2e1">
            <ModelPerformance data-id="element-1159" data-mp="1bs71lhsv5rps" />
            <SectionCard title="How scoring is governed" description="The assurance position on every AI output in the platform" data-id="element-1160" data-mp="1bsyyqx23zxi6">
              <ul className="grid gap-3 text-[12px] leading-relaxed text-muted-foreground md:grid-cols-3" data-id="element-1161" data-mp="1li8s0g8ovm7v">
                <li className="rounded-sm border border-border bg-muted/40 px-3 py-2.5" data-id="element-1162" data-mp="xs4sajymg6m5">
                  <span className="block text-[13px] font-medium text-foreground" data-id="element-1163" data-mp="stl1a55684mx">
                    Advisory only
                  </span>
                  No record advances, is approved or is declined by the model. Officers record every
                  decision with a rationale.
                </li>
                <li className="rounded-sm border border-border bg-muted/40 px-3 py-2.5" data-id="element-1164" data-mp="2i7zp8n8d7nhd">
                  <span className="block text-[13px] font-medium text-foreground" data-id="element-1165" data-mp="2d9ewt9zcu83h">
                    Evidence-linked
                  </span>
                  Every criterion score cites the submitted evidence it was derived from, and every
                  recommendation names the preset rule that triggered it.
                </li>
                <li className="rounded-sm border border-border bg-muted/40 px-3 py-2.5" data-id="element-1166" data-mp="2bt10w6c0ggu7">
                  <span className="block text-[13px] font-medium text-foreground" data-id="element-1167" data-mp="2gs5bn1el2mmn">
                    Fully attributable
                  </span>
                  Acceptances, overrides and gate overrides are written to the record timeline with
                  the officer's name and rationale.
                </li>
              </ul>
            </SectionCard>
          </TabsContent>

          <TabsContent value="reporting" className="mt-4 space-y-4" data-id="element-1168" data-mp="2zpetie49fs1s">
            <SectionCard title="Generated reports" description={`${reports.length} reports in the archive. Generate a new one from the intelligence hub.`} actions={<Link to="/intelligence" className="inline-flex items-center rounded-sm bg-uae-navy px-2.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-uae-sky" data-id="element-1170" data-mp="1s3zw0l25ea9a">
                  <FileText className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-1171" data-mp="1iezsqpgm4spa" />
                  Generate report
                </Link>} bodyClassName="p-0" data-id="element-1169" data-mp="2znlqr9wrprz2">
              <ul className="divide-y divide-border" data-id="element-1172" data-mp="2b1sth7u05rza">
                {reports.map(report => <li key={report.id} data-id="element-1173" data-mp="2652i2xt57kk7">
                    <Link to="/reports" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-muted" data-id="element-1174" data-mp="261y5pd5ixiig">
                      <div className="min-w-0" data-id="element-1175" data-mp="1x570ob8zutbq">
                        <p className="truncate text-[13px] font-medium" data-id="element-1176" data-mp="qnaez4i9ysrs">{report.title}</p>
                        <p className="text-[11px] text-muted-foreground" data-id="element-1177" data-mp="lnec58lbd7dt">
                          {reportTypeLabels[report.type]} · {report.scope}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-[11px] text-muted-foreground" data-id="element-1178" data-mp="329kq3d3yeu0e">
                        <span data-id="element-1179" data-mp="36bpfu0ra76hr">{report.audience}</span>
                        <span className="font-mono" data-id="element-1180" data-mp="369uvanjx9ge7">{formatDate(report.createdOn)}</span>
                      </div>
                    </Link>
                  </li>)}
              </ul>
            </SectionCard>

            {latestReport && <div data-id="element-1181" data-mp="3kzsppl8g32bu">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground" data-id="element-1182" data-mp="3kzala1bmphsu">
                  Most recent report
                </p>
                <ReportDocument report={latestReport} data-id="element-1183" data-mp="3u1th41gzyu5l" />
              </div>}
          </TabsContent>
        </Tabs>
      </div>
    </div>;
}